<template>
  <div>
    <div class="pa-3">
      <!-- Mobile-only toggle bar for stat cards -->
      <v-row v-if="$vuetify.display.xs" class="justify-center pa-1 pb-0 mb-0">
      <v-toolbar
        flat
        density="compact"
        color="grey-lighten-3"
        class="d-sm-none rounded mx-n2"
        style="cursor:pointer"
        @click="mobileStatsOpen = !mobileStatsOpen"
      >
        <v-icon class="ml-1 mr-2" size="18">mdi-view-grid-outline</v-icon>
        <v-toolbar-title class="text-body-2 font-weight-medium">
          {{ $t("history.general_details") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon variant="text" size="small">
          <v-icon>
            {{ mobileStatsOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-btn>
      </v-toolbar>
</v-row>
      <!-- CARS -->
      <v-row class="mt-0">
        <template v-if="!$vuetify.display.xs || mobileStatsOpen">
        <v-col cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails.cars !== null"
            color="#1F7087"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.cars) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.inserted_vehicles") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-car-multiple </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/vehicle-entry' })"
              >
                {{ $t("history.go_to_cars") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#1F7087" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#1F7087" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- PRICE -->
        <v-col cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails.price !== null"
            color="warning darken-1"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="pr-0 mr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.price) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.inventory_price") }}
                  </div>
                  <div
                    class="white--text pointer d-none d-sm-block"
                    @click="$router.push('/inventory?price=1')"
                  >
                    <span
                      class="blue--text text--darken-2 text-decoration-underline"
                      style="font-size: 12px; font-weight: bold"
                    >
                      {{ formatCount(generalDetails.partsWithPrice1) }}
                      {{ $t("history.parts_with_price_1") }} ≈{{
                        (
                          (generalDetails.partsWithPrice1 /
                            generalDetails.parts) *
                          100
                        ).toFixed(2)
                      }}%
                    </span>
                  </div>
                </v-col>
                <v-col cols="auto" class="mt-0 pt-0">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-currency-eur </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/inventory' })"
              >
                {{ $t("history.go_to_inventory") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="warning darken-1" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="warning darken-4" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- PARTS -->
        <v-col cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails.parts !== null"
            color="red-darken-1"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="mr-0 pr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.parts) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.inventory_parts") }}
                  </div>
                  <div class="white--text d-none d-sm-block">
                    <strong style="font-size: 13px">
                      {{ $t("history.parts_on_ebay") }}:
                      {{
                        generalDetails.partsOnEbay && generalDetails.parts
                          ? (
                              (generalDetails.partsOnEbay /
                                generalDetails.parts) *
                              100
                            ).toFixed(2) + "%"
                          : ""
                      }}
                    </strong>
                  </div>
                </v-col>
                <v-col cols="auto" class="mt-0 pt-0">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-package-variant </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/inventory' })"
              >
                {{ $t("history.go_to_inventory") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="red-darken-1" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="red-darken-4" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- SALES -->
        <v-col cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails.sales !== null"
            color="#308333"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.sales) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.sales") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-basket-outline </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/sales' })"
              >
                {{ $t("history.go_to_sales") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#308333" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="green darken-4" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- PARTS WITHOUT APPLICABILITY -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'SuperAdmin'
          "
          cols="6"
          sm="6"
          md="3"
          class="pa-1"
        >
          <v-card
            v-if="generalDetails.partsWithoutAppl !== null"
            color="deep-orange darken-1"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="mr-0 pr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.partsWithoutAppl) }}
                  </div>
                  <div class="white--text" style="font-size: 13px">
                    {{ $t("history.parts_without_applicability") }}
                    <span class="d-none d-sm-inline">≈{{
                      (
                        (generalDetails.partsWithoutAppl /
                          generalDetails.parts) *
                        100
                      ).toFixed(2)
                    }}%</span>
                  </div>
                </v-col>
                <v-col cols="auto" class="d-none d-sm-flex">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-help-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="
                  $router.push({ path: '/inventory', query: { noappl: true } })
                "
              >
                {{ $t("history.go_to_inventory") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="deep-orange darken-1" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="deep-orange darken-4"
                />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- PARTS WITHOUT APPLICABILITY FOR INSTITUTION -->
        <v-col v-else cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails.userPartsWithoutAppl !== null"
            color="deep-orange darken-1"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="mr-0 pr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.userPartsWithoutAppl) }}
                  </div>
                  <div class="white--text" style="font-size: 13px">
                    {{ $t("history.parts_without_applicability") }}
                    <span class="d-none d-sm-inline">≈{{
                      (
                        (generalDetails.userPartsWithoutAppl /
                          generalDetails.parts) *
                        100
                      ).toFixed(2)
                    }}%</span>
                  </div>
                </v-col>
                <v-col cols="auto"  class="d-none d-sm-flex">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-help-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="
                  $router.push({ path: '/inventory', query: { noappl: true } })
                "
              >
                {{ $t("history.go_to_inventory") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="deep-orange darken-1" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="deep-orange darken-4"
                />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- INSTITUTIONS -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'SuperAdmin'
          "
          cols="6"
          sm="6"
          md="3"
          class="pa-1"
        >
          <v-card
            v-if="generalDetails.activeInstitutions !== null"
            color="#607e3d"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <v-row align="center" class="pl-3 pb-2 pt-3">
                    <div class="display-1 font-weight-bold white--text">
                      {{ formatCount(generalDetails.activeInstitutions) }}
                    </div>
                    <v-icon large class="d-none d-sm-inline-flex"> check_circle </v-icon>
                  </v-row>
                  <div class="subheading white--text pt-1  d-sm-block">
                    {{ $t("history.of") }} {{ formatCount(generalDetails.institutions) }}
                    {{ $t("history.institutions") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-domain </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/institutions' })"
              >
                {{ $t("history.go_to_institutions") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#607e3d" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#607e3d" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- ORDERS IINSTITUTION -->
        <v-col v-else cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails && generalDetails.orderCount !== null"
            color="#607e3d"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <v-row align="center" class="pl-3 pb-2 pt-3">
                    <div class="display-1 font-weight-bold white--text">
                      {{ formatCount(generalDetails.orderCount) }}
                    </div>
                    <v-icon large class="d-none d-sm-inline-flex"> check_circle </v-icon>
                  </v-row>
                  <div class="subheading white--text pt-1">
                    {{ $t("history.orders") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white"> mdi-cart-plus </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/ordered' })"
              >
                {{ $t("history.go_to_orders") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card v-else color="#607e3d" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#607e3d" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- USERS -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'SuperAdmin'
          "
          cols="6"
          sm="6"
          md="3"
          class="pa-1"
        >
          <v-card
            v-if="generalDetails.users"
            color="#144b77"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.users) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.users") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-account-multiple-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/users' })"
              >
                {{ $t("history.go_to_users") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#144b77" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#144b77" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- INSTITUTION USERS -->
        <v-col v-else cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="generalDetails && generalDetails.institutionUsers !== null"
            color="#144b77"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col>
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.institutionUsers) }}
                  </div>
                  <div class="subheading white--text">
                    {{ $t("history.users") }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-account-multiple-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/users' })"
              >
                {{ $t("history.go_to_users") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#144b77" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#144b77" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- PARTS AWAITING APPROVAL -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'SuperAdmin'
          "
          cols="6"
          sm="6"
          md="3"
          class="pa-1"
        >
          <v-card
            v-if="generalDetails.tempPartsAwaitAppr !== null"
            color="#c1770b"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="mr-0 pr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.tempPartsAwaitAppr) }}
                  </div>
                  <div class="white--text">
                    {{ $t("history.parts_awaiting_approval") }}
                  </div>
                  <div
                    v-if="
                      generalDetails.tempPartsAwaitApprConnectedWithCar !=
                        null &&
                      generalDetails.tempPartsAwaitApprNotConnectedWithCar !=
                        null
                    "
                    class="white--text mt-1 d-none d-sm-block"
                  >
                    <strong style="font-size: 13px">
                      {{ $t("history.temp_parts_with_vehicle") }}:
                      {{ formatCount(generalDetails.tempPartsAwaitApprConnectedWithCar) }}
                    </strong>
                    <br />
                    <strong style="font-size: 13px">
                      {{ $t("history.temp_parts_without_vehicle") }}:
                      {{ formatCount(generalDetails.tempPartsAwaitApprNotConnectedWithCar) }}
                    </strong>
                  </div>
                </v-col>
                <v-col cols="auto" class="d-none d-sm-block">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-receipt-clock-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/approve-parts' })"
              >
                {{ $t("history.go_to_approve_parts") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#c1770b" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#c1770b" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!-- TEMPORARY PARTS -->
        <v-col v-else cols="6" sm="6" md="3" class="pa-1">
          <v-card
            v-if="
              generalDetails && generalDetails.institutionTempParts !== null
            "
            color="#c1770b"
            flat
            class="fill-height d-flex flex-column"
          >
            <v-card-text class="flex-grow-1">
              <v-row align="center">
                <v-col class="mr-0 pr-0">
                  <div class="display-1 font-weight-bold white--text">
                    {{ formatCount(generalDetails.institutionTempParts) }}
                  </div>
                  <div class="white--text">
                    {{ $t("history.temporary_parts") }}
                  </div>
                  <div
                    v-if="
                      generalDetails.institutionTempPartsConnectedWithCar !=
                        null &&
                      generalDetails.institutionTempPartsNotConnectedWithCar !=
                        null
                    "
                    class="white--text mt-1 d-none d-sm-block"
                  >
                    <strong style="font-size: 13px">
                      {{ $t("history.temp_parts_with_vehicle") }}:
                      {{ formatCount(generalDetails.institutionTempPartsConnectedWithCar) }}
                    </strong>
                    <br />
                    <strong style="font-size: 13px">
                      {{ $t("history.temp_parts_without_vehicle") }}:
                      {{
                        formatCount(generalDetails.institutionTempPartsNotConnectedWithCar)
                      }}
                    </strong>
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-icon :size="$vuetify.display.xs ? '22' : '50'" color="white">
                    mdi-receipt-clock-outline
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider class="d-none d-sm-block" />
            <v-card-actions
              class="justify-center d-none d-sm-flex"
              style="background-color: rgba(0, 0, 0, 0.12)"
            >
              <v-btn
                block
                text
                small
                class="transparent-btn"
                @click="$router.push({ path: '/insert-parts' })"
              >
                {{ $t("history.go_to_temporary_parts") }}
                <v-icon right> mdi-menu-right </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card v-else color="#c1770b" flat>
            <v-card-text>
              <v-row align="center" justify="center">
                <v-progress-circular indeterminate color="#c1770b" />
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        </template>
      </v-row>

      <!-- Mobile-only toggle bar for admin panel (same level as Panoramica toolbar) -->
           <v-row         
           v-if="$vuetify.display.xs && $store.getters.role === 'InstitutionAdmin'"
 class="justify-center pa-1 pb-0 mb-0">

      <v-toolbar
        flat
        density="compact"
        color="grey-lighten-3"
        class="d-sm-none rounded mt-0 pt-0 mb-0 pb-0 mx-n2"
        style="cursor:pointer"
        @click="mobileAdminOpen = !mobileAdminOpen"
      >
        <v-icon class="ml-1 mr-2" size="18">mdi-tools</v-icon>
        <v-toolbar-title class="text-body-2 font-weight-medium">
          {{ $t("home.price_ebay_tools") }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon variant="text" size="small">
          <v-icon>
            {{ mobileAdminOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </v-btn>
      </v-toolbar>
</v-row>
      <!-- ADMIN PANEL -->
      <v-row>
        <v-col
          v-if="$store.getters.role === 'InstitutionAdmin'"
          v-show="!$vuetify.display.xs || mobileAdminOpen"
          cols="12"
          class="pa-1 mx-2"
        >
          <v-card
            color="grey-lighten-3"
            flat
            class="pa-1 pt-3"
          >
            <v-card-text class="pt-3">
              <div>
                <v-row>
                  <v-col cols="7" sm="2" md="2">
                    <v-text-field
                      v-model="priceChange.percentage"
                      type="number"
                      min="0"
                      label="%"
                      variant="outlined"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="5" sm="6" md="2">
                    <v-btn
                      variant="flat"
                      :color="priceChange.type === 'decrease' ? 'primary' : ''"
                      class="mr-1 mt-2"
                      @click="priceChange.type = 'decrease'"
                    >
                      <v-icon>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn
                      variant="flat"
                      :color="priceChange.type === 'increase' ? 'primary' : ''"
                      class="mt-2"
                      @click="priceChange.type = 'increase'"
                    >
                      <v-icon>mdi-arrow-up</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-btn
                      block
                      color="red-darken-2"
                      class="text-white mt-2 change-inventory-prices-btn"
                      :disabled="
                        !priceChange.percentage || priceChange.percentage < 1
                      "
                      :loading="priceChange.loading"
                      @click="changeInventoryPrices"
                    >
                      {{ $t("change_inventory_prices") }}
                    </v-btn>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    class="d-flex flex-wrap ga-2 align-end justify-end mb-4"
                    style="min-width: 0"
                  >
                    <v-btn
                      color="red-darken-2"
                      class="text-white mt-2"
                      :loading="exportEbayShippingLoading"
                      @click="downloadExcel"
                    >
                      {{ $t("export_ebay_shipping_data") }}
                      <v-icon class="ml-1">mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="
                        $store.getters.role === 'LekoAdmin' ||
                        $store.getters.role === 'LekoEmployee' ||
                        $store.getters.role === 'InstitutionAdmin' ||
                        $store.getters.role === 'SuperAdmin' ||
                        $store.getters.institutionExportEbayLeko
                      "
                      color="red-darken-2"
                      class="text-white mt-2"
                      @click="$router.push({ name: 'shipping-bulk-import' })"
                    >
                      {{ $t("shippingBulkImport.label") }}
                      <v-icon class="ml-1">mdi-file-upload-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row
                  v-if="institution && institution.export_ebay_leko"
                  class="pa-2 pt-0 mt-1"
                >
                  <v-col cols="12" md="12" lg="12" class="pa-0 ma-0">
                    <v-col
                      cols="12"
                      md="7"
                      lg="7"
                      sm="12"
                      xs="12"
                      class="pa-0 ma-0"
                    >
                      <v-alert
                        v-model="priceInfo"
                        class="mb-0"
                        type="error"
                        variant="tonal"
                        border="bottom"
                      >
                        {{ $t("price_change_info") }}
                      </v-alert>
                    </v-col>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- MAP -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'SuperAdmin'
          "
          cols="12"
        >
          <iframe
            src="https://www.google.com/maps/d/embed?mid=1fuUZWVQP-mqrgu9boyVhmVajY6DeIRg&ehbc=2E312F"
            width="100%"
            height="500px"
            frameborder="0"
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
          />
        </v-col>

        <!-- CHART -->
        <v-col
          v-if="
            $store.getters.role === 'LekoAdmin' ||
            $store.getters.role === 'InstitutionAdmin' ||
            $store.getters.role === 'SuperAdmin' ||
            $store.getters.role === 'LekoEmployee'
          "
          cols="12"
          class="pa-1"
        >
          <v-card color="grey-lighten-3" flat>
            <v-card-actions class="px-4">
              <v-row>
                <v-col :cols="$vuetify.display.smAndUp ? 'auto' : 12">
                  <!-- Mobile: chips -->
                  <v-chip-group v-if="$vuetify.display.xs" class="flex-wrap pa-0">
                    <v-chip
                      size="small"
                      :color="
                        chart.content === 'sales' && chart.status === '6.0'
                          ? 'green-darken-1'
                          : ''
                      "
                      :variant="
                        chart.content === 'sales' && chart.status === '6.0'
                          ? 'flat'
                          : 'outlined'
                      "
                      @click="selectChartContent('sales', '6.0')"
                    >
                      {{ $t("history.sales") }}
                      <v-icon end size="x-small">mdi-basket-outline</v-icon>
                    </v-chip>
                    <!-- <v-chip
                      size="small"
                      :color="
                        chart.content === 'sales' && chart.status === '6.2'
                          ? 'green-darken-2'
                          : ''
                      "
                      :variant="
                        chart.content === 'sales' && chart.status === '6.2'
                          ? 'flat'
                          : 'outlined'
                      "
                      @click="selectChartContent('sales', '6.2')"
                    >
                      {{ $t("part_status.sold_ebay") }}
                      <v-icon end size="x-small">mdi-tag-outline</v-icon>
                    </v-chip> -->
                    <v-chip
                      size="small"
                      :color="
                        chart.content === 'sales' && chart.status === '3.0'
                          ? 'blue-darken-1'
                          : ''
                      "
                      :variant="
                        chart.content === 'sales' && chart.status === '3.0'
                          ? 'flat'
                          : 'outlined'
                      "
                      @click="selectChartContent('sales', '3.0')"
                    >
                      {{ $t("part_status.returned_sales") }}
                      <v-icon end size="x-small">mdi-arrow-left-right</v-icon>
                    </v-chip>
                    <v-chip
                      size="small"
                      :color="
                        chart.content === 'sales' && chart.status === '8.1'
                          ? 'red-darken-1'
                          : ''
                      "
                      :variant="
                        chart.content === 'sales' && chart.status === '8.1'
                          ? 'flat'
                          : 'outlined'
                      "
                      @click="selectChartContent('sales', '8.1')"
                    >
                      {{ $t("part_status.damaged_sales") }}
                      <v-icon end size="x-small">mdi-alert-outline</v-icon>
                    </v-chip>
                    <v-chip
                      size="small"
                      :color="chart.content === 'parts' ? 'red-darken-1' : ''"
                      :variant="chart.content === 'parts' ? 'flat' : 'outlined'"
                      @click="selectChartContent('parts', '2.0')"
                    >
                      {{ $t("history.parts") }}
                      <v-icon end size="x-small">mdi-package-variant</v-icon>
                    </v-chip>
                    <v-chip
                      size="small"
                      :color="chart.content === 'ebay-leko' ? 'green-darken-1' : ''"
                      :variant="chart.content === 'ebay-leko' ? 'flat' : 'outlined'"
                      @click="selectChartContent('ebay-leko', '6.4')"
                    >
                      {{ $t("history.ebay_sales") }}
                      <img class="ml-1" height="12" src="@/assets/icons/ebay_colored.png" alt="eBay" />
                    </v-chip>
                    <v-chip
                      size="small"
                      :color="chart.content === 'entered_parts' ? 'orange-darken-1' : ''"
                      :variant="chart.content === 'entered_parts' ? 'flat' : 'outlined'"
                      @click="selectChartContent('entered_parts', '1')"
                    >
                      {{ $t("history.entered_parts") }}
                    </v-chip>
                  </v-chip-group>
                  <!-- Desktop: buttons (unchanged) -->
                  <template v-if="!$vuetify.display.xs">
                    <v-btn
                      :variant="chart.content === 'sales' && chart.status === '6.0' ? 'flat' : 'text'"
                      color="green-darken-1"
                      dark
                      @click="selectChartContent('sales', '6.0')"
                    >
                      {{ $t("history.sales") }}
                      <v-icon end> mdi-basket-outline </v-icon>
                    </v-btn>
                    <!-- <v-btn
                      :variant="chart.content === 'sales' && chart.status === '6.2' ? 'flat' : 'text'"
                      color="green-darken-2"
                      dark
                      @click="selectChartContent('sales', '6.2')"
                    >
                      {{ $t("part_status.sold_ebay") }}
                    </v-btn> -->
                    <v-btn
                      :variant="chart.content === 'sales' && chart.status === '3.0' ? 'flat' : 'text'"
                      color="blue-darken-1"
                      dark
                      @click="selectChartContent('sales', '3.0')"
                    >
                      {{ $t("part_status.returned_sales") }}
                    </v-btn>
                    <v-btn
                      :variant="chart.content === 'sales' && chart.status === '8.1' ? 'flat' : 'text'"
                      color="red-darken-1"
                      dark
                      @click="selectChartContent('sales', '8.1')"
                    >
                      {{ $t("part_status.damaged_sales") }}
                    </v-btn>
                    <v-btn
                      :variant="chart.content !== 'parts' ? 'text' : 'flat'"
                      color="red-darken-1"
                      dark
                      @click="selectChartContent('parts', '2.0')"
                    >
                      {{ $t("history.parts") }}
                      <v-icon end> mdi-package-variant </v-icon>
                    </v-btn>
                    <v-btn
                      :variant="chart.content !== 'ebay-leko' ? 'text' : 'flat'"
                      color="green-darken-1"
                      dark
                      @click="selectChartContent('ebay-leko', '6.4')"
                    >
                      {{ $t("history.ebay_sales") }}
                      <img
                        class="ml-3"
                        height="24"
                        src="@/assets/icons/ebay_colored.png"
                        alt="eBay"
                      />
                    </v-btn>
                    <v-btn
                      :variant="chart.content !== 'entered_parts' ? 'text' : 'flat'"
                      color="orange-darken-1"
                      dark
                      @click="selectChartContent('entered_parts', '1')"
                    >
                      {{ $t("history.entered_parts") }}
                    </v-btn>
                  </template>
                </v-col>
                <v-col class="text-end d-none d-sm-flex align-center justify-end">
                  <v-btn
                    variant="flat"
                    :color="chart.type === 'line' ? 'primary' : ''"
                    class="mr-1"
                    @click="selectChartType('line')"
                  >
                    {{ $t("history.line") }}
                  </v-btn>
                  <v-btn
                    variant="flat"
                    :color="chart.type === 'bar' ? 'primary' : ''"
                    @click="selectChartType('bar')"
                  >
                    {{ $t("history.bar") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>

            <v-divider />

            <v-card-text>
              <!-- Mobile-only: compact date picker + Line/Bar -->
              <v-row
                v-if="$vuetify.display.xs && !ebay_overview"
                align="center"
                class="px-1 pb-1"
              >
                <v-col class="flex-grow-1 pr-1">
                  <v-menu v-model="mobileDateMenu" :close-on-content-click="false">
                    <template #activator="{ props }">
                      <v-text-field
                        v-bind="props"
                        :model-value="formattedDate"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        hide-details
                        variant="outlined"
                        density="compact"
                      />
                    </template>
                    <v-date-picker
                      v-model="chart.time"
                      :type="chart.timePeriod === 'Month' ? 'month' : 'date'"
                      color="red-darken-2"
                      reactive
                      @update:model-value="mobileDateMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="auto" class="pl-0">
                  <v-btn
                    variant="flat"
                    size="small"
                    :color="chart.type === 'line' ? 'primary' : ''"
                    class="mr-1"
                    @click="selectChartType('line')"
                  >
                    {{ $t("history.line") }}
                  </v-btn>
                  <v-btn
                    variant="flat"
                    size="small"
                    :color="chart.type === 'bar' ? 'primary' : ''"
                    @click="selectChartType('bar')"
                  >
                    {{ $t("history.bar") }}
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Desktop layout -->
              <v-row align="center">
                <v-col v-if="!ebay_overview" cols="12" md="4" class="d-none d-sm-flex flex-column">
                  <v-row>
                    <v-col md="8" cols="auto" class="flex-grow-1 ml-5">
                      <v-select
                        v-model="chart.timePeriod"
                        :items="timePeriodItems"
                        :label="$t('home.charts.time_period')"
                        :hint="$t('home.charts.select_time_period')"
                        variant="solo"
                        flat
                        item-title="title"
                        item-value="value"
                        persistent-hint
                        color="red-darken-2"
                        background-color="grey-lighten-4"
                        class="pl-5"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <v-btn variant="text" icon @click="getPastUserActivity()">
                        <v-icon color="grey-darken-2">
                          mdi-orbit-variant
                        </v-icon>
                        
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row justify="center">
                    <v-date-picker
                      v-model="chart.time"
                      :type="chart.timePeriod === 'Month' ? 'month' : 'date'"
                      color="red-darken-2"
                      reactive
                    />
                  </v-row>
                </v-col>
                <v-col v-if="!ebay_overview" cols="12" md="8">
                  <v-row v-if="chart.loading" justify="center">
                    <v-progress-circular size="70" color="red" indeterminate />
                  </v-row>
                  <chart
                    v-if="!chart.loading"
                    :type="chart.type"
                    :data="chart.data"
                    :class="$vuetify.display.xs ? '' : 'mt-12'"
                  />
                </v-col>
              </v-row>
              <v-main
                v-if="
                  institution &&
                  institution.export_ebay_leko &&
                  chart.content === 'ebay-leko' &&
                  $store.getters.role !== 'LekoAdmin' &&
                  $store.getters.role !== 'SuperAdmin' &&
                  $store.getters.role !== 'LekoEmployee'
                "
                class="pa-4 ml-5 mt-3"
              >
                <v-card class="mx-auto rounded-lg" elevation="4">
                  <v-card-text v-if="ebay_overview_loader" class="text-center">
                    <v-progress-circular size="70" color="red" indeterminate />
                  </v-card-text>
                  <v-card-text
                    v-else-if="
                      standardsProfiles && standardsProfiles.length > 0
                    "
                    class="pa-6"
                  >
                    <v-row>
                      <v-col cols="12">
                        <v-tabs
                          v-model="activeTab"
                          background-color="grey-lighten-4"
                          grow
                        >
                          <v-tab
                            v-for="(program, index) in uniquePrograms"
                            :key="program"
                            :style="{
                              backgroundColor:
                                activeTab === index
                                  ? getStandardColor(
                                      mapStandardLevel(
                                        getCycle(program, 'CURRENT')
                                          .standardsLevel,
                                      ),
                                    )
                                  : '',
                            }"
                            :class="{ 'white--text': activeTab === index }"
                          >
                            <img
                              src="@/assets/icons/ebay_colored.png"
                              height="34"
                              class="mr-2"
                              alt="eBay"
                            />
                            <span class="font-weight-bold">{{
                              $t("ebay_overview.title")
                            }}</span>
                          </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="activeTab" class="mt-2">
                          <v-tab-item
                            v-for="program in uniquePrograms"
                            :key="program"
                          >
                            <v-row dense>
                              <v-col cols="12" md="12">
                                <v-row dense>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      business
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.business_name")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      {{ institution.eBayDetails.userId }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      supervisor_account
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.managed_tier")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      {{ seller.managedTier }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1"> event </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.eval_month_current")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      {{
                                        getCycle(program, "CURRENT").cycle
                                          .evaluationMonth || "N/A"
                                      }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1"> event </v-icon>
                                    <strong
                                      >{{
                                        $t(
                                          "ebay_overview.eval_month_projected",
                                        )
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      {{
                                        getCycle(program, "PROJECTED").cycle
                                          .evaluationMonth || "N/A"
                                      }}
                                    </div>
                                  </v-col>
                                </v-row>
                                <v-row dense>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      shopping_cart
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.transactions")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      <strong>{{
                                        getMetric(
                                          getCycle(program, "CURRENT"),
                                          "MIN_TXN_COUNT",
                                        ).value || "N/A"
                                      }}</strong>
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      attach_money
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.total_sales")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      <strong>
                                        {{
                                          getMetric(
                                            getCycle(program, "CURRENT"),
                                            "MIN_GMV",
                                          ).value
                                            ? getMetric(
                                                getCycle(program, "CURRENT"),
                                                "MIN_GMV",
                                              ).value.value || "N/A"
                                            : "N/A"
                                        }}
                                        {{
                                          getMetric(
                                            getCycle(program, "CURRENT"),
                                            "MIN_GMV",
                                          ).value
                                            ? getMetric(
                                                getCycle(program, "CURRENT"),
                                                "MIN_GMV",
                                              ).value.currencyCodeEnum || ""
                                            : ""
                                        }}
                                      </strong>
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      analytics
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.actual_standard")
                                      }}:</strong
                                    >
                                    <div class="ml-3">
                                      <v-chip
                                        :color="
                                          getStandardColor(
                                            mapStandardLevel(
                                              getCycle(program, 'CURRENT')
                                                .standardsLevel,
                                            ),
                                          )
                                        "
                                        text-color="white"
                                        class="ma-1 px-1"
                                        small
                                        pill
                                      >
                                        {{
                                          mapStandardLevel(
                                            getCycle(program, "CURRENT")
                                              .standardsLevel,
                                          )
                                        }}
                                      </v-chip>
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      analytics
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.predicted_standard")
                                      }}:</strong
                                    >
                                    <div class="ml-3">
                                      <v-chip
                                        :color="
                                          getStandardColor(
                                            mapStandardLevel(
                                              getCycle(program, 'PROJECTED')
                                                .standardsLevel,
                                            ),
                                          )
                                        "
                                        text-color="white"
                                        class="ma-1 px-1"
                                        small
                                        pill
                                      >
                                        {{
                                          mapStandardLevel(
                                            getCycle(program, "PROJECTED")
                                              .standardsLevel,
                                          )
                                        }}
                                      </v-chip>
                                    </div>
                                  </v-col>
                                </v-row>
                                <v-row dense>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1"> today </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.min_days")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      {{
                                        getMetric(
                                          getCycle(program, "CURRENT"),
                                          "MIN_DAYS_ON_SITE",
                                        ).value || "N/A"
                                      }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      verified_user
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t(
                                          "ebay_overview.business_seller_criteria",
                                        )
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      <v-icon>{{
                                        getMetric(
                                          getCycle(program, "CURRENT"),
                                          "BUSINESS_SELLER",
                                        ).value
                                          ? "check"
                                          : "cancel"
                                      }}</v-icon>
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      shopping_cart
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t(
                                          "ebay_overview.predicted_transactions",
                                        )
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      <strong>{{
                                        getMetric(
                                          getCycle(program, "PROJECTED"),
                                          "MIN_TXN_COUNT",
                                        ).value || "N/A"
                                      }}</strong>
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="3" lg="3">
                                    <v-icon small class="mr-1">
                                      attach_money
                                    </v-icon>
                                    <strong
                                      >{{
                                        $t("ebay_overview.predicted_sales")
                                      }}:</strong
                                    >
                                    <div class="ml-4">
                                      <strong>
                                        {{
                                          getMetric(
                                            getCycle(program, "PROJECTED"),
                                            "MIN_GMV",
                                          ).value
                                            ? getMetric(
                                                getCycle(program, "PROJECTED"),
                                                "MIN_GMV",
                                              ).value.value || "N/A"
                                            : ""
                                        }}
                                        {{
                                          getMetric(
                                            getCycle(program, "PROJECTED"),
                                            "MIN_GMV",
                                          ).value
                                            ? getMetric(
                                                getCycle(program, "PROJECTED"),
                                                "MIN_GMV",
                                              ).value.currencyCodeEnum || ""
                                            : ""
                                        }}
                                      </strong>
                                    </div>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-tab-item>
                        </v-tabs-items>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <!-- <v-card-text v-else-if="ebay_error">
                    <v-alert type="error" variant="tonal" border="bottom">
                      {{ ebay_error }}
                    </v-alert>
                  </v-card-text> -->
                </v-card>
              </v-main>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <v-dialog v-model="confirmDialog" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :value="true"
            type="warning"
            variant="tonal"
            border="bottom"
          >
            {{ $t("confirm_alert") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn
            color="warning"
            outlined
            :loading="priceChangeLoading"
            @click="confirmChangePrice()"
          >
            {{ $t("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="successDialog" width="250px">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :value="true"
            type="success"
            variant="tonal"
            border="bottom"
          >
            {{ $t("institutions.form.success") }}
          </v-alert>
        </v-card-title>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog" width="250px">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :value="true"
            type="error"
            variant="tonal"
            border="bottom"
          >
            Error
          </v-alert>
        </v-card-title>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";
import * as XLSX from "xlsx";
import Chart from "../chart.vue";

function generateRandomNumbers() {
  const arr = [];
  for (let a = 0; a < 3; a += 1) {
    arr.push(Math.floor(Math.random() * 256));
  }
  return arr;
}

export default {
  components: {
    chart: Chart,
  },
  data: () => ({
    mobileStatsOpen: true,
    mobileAdminOpen: false,
    mobileDateMenu: false,
    chart: {
      loading: true,
      data: null,
      content: "sales",
      status: "2.0",
      type: "bar",
      timePeriod: "Month",
      time: new Date().toISOString().substr(0, 7),
    },
    timePeriodItems: [
      { title: "Month", value: "Month" },
      { title: "Week", value: "Week" },
      { title: "Day", value: "Day" },
    ],
    generalDetails: {
      cars: null,
      price: null,
      parts: null,
      sales: null,
      partsWithoutAppl: null,
      activeInstitutions: null,
      institutions: null,
      tempPartsAwaitAppr: null,
      tempPartsAwaitApprConnectedWithCar: null,
      tempPartsAwaitApprNotConnectedWithCar: null,
      partsWithPrice1: null,
      partsOnEbay: null,
      institutionTempParts: null,
      institutionTempPartsConnectedWithCar: null,
      institutionTempPartsNotConnectedWithCar: null,
      institutionUsers: null,
      userPartsWithoutAppl: null,
      orderCount: null,
    },
    priceChange: {
      percentage: 0,
      type: "increase",
      loading: false,
    },
    ebay_overview: false,
    ebay_overview_loader: true,
    seller: {
      businessName: "",
      managedTier: "Managed",
    },
    institution: null,
    standardsProfiles: [],
    activeTab: 0,
    ebay_error: null,
    exportEbayShippingLoading: false,
    confirmDialog: false,
    priceInfo: true,
    priceChangeLoading: false,
    successDialog: false,
    errorDialog: false,
  }),
  computed: {
    formattedDate() {
      if (!this.chart || !this.chart.time) return "";
      let date;
      if (this.chart.time instanceof Date) {
        date = this.chart.time;
      } else {
        const str = this.chart.time;
        date =
          this.chart.timePeriod === "Month"
            ? new Date(str + "-01")
            : new Date(str);
      }
      if (!date || isNaN(date.getTime())) return "";
      return date.toLocaleDateString(
        this.$i18n.locale,
        this.chart.timePeriod === "Month"
          ? { month: "short", year: "numeric" }
          : { day: "2-digit", month: "short", year: "numeric" },
      );
    },
    pastWeek() {
      return new Date(Date.now() - 604800000).toISOString().split("T")[0];
    },
    uniquePrograms() {
      if (this.standardsProfiles.length > 0) {
        const programs = this.standardsProfiles
          .map((p) => p.program)
          .filter((p) => p === "PROGRAM_GLOBAL");
        return [...new Set(programs)];
      }
      return [];
    },
  },
  watch: {
    "chart.time": {
      handler(value) {
        // Vuetify 3 date-picker emits a Date object — normalize it to a string
        if (value instanceof Date) {
          if (this.chart.timePeriod === "Month") {
            const y = value.getFullYear();
            const m = String(value.getMonth() + 1).padStart(2, "0");
            this.chart.time = `${y}-${m}`;
          } else {
            this.chart.time = value.toISOString().split("T")[0];
          }
          return; // watcher will re-fire with the normalized string
        }
        if (this.chart && this.chart.data && !this.chart.loading) {
          this.getPastUserActivity();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getLocale();
  },
  async created() {
    this.selectChartContent("sales", "6.0");
    await this.fetchInstitutionDetails();
    this.getGeneralDetails();
    this.getPastUserActivity();
    this.getEbayOverview();
  },
  methods: {
    formatCount(value) {
      if (value === null || value === undefined || value === "") return value;
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return Number(num.toFixed(0)).toLocaleString("de-DE");
    },
    resolveEnteredPartsFlag(explicitValue) {
      // When called from template events (e.g. @click="fn"), Vue passes the event object.
      // We only accept booleans; otherwise derive from the currently selected chart content.
      if (typeof explicitValue === "boolean") return explicitValue;
      return (
        this.chart &&
        this.chart.content === "entered_parts" &&
        this.chart.status === "1"
      );
    },
    getLocale() {
      this.timePeriodItems[0].title = this.$t(
        "home.charts.time_period_items.month",
      );
      this.timePeriodItems[1].title = this.$t(
        "home.charts.time_period_items.week",
      );
      this.timePeriodItems[2].title = this.$t(
        "home.charts.time_period_items.day",
      );
    },
    async getGeneralDetails() {
      try {
        const response = await getRequest(
          `/history/user/general/${this.$store.getters.userId}`,
        );
        this.generalDetails = response;
      } catch {}
    },
    async getPastUserActivity(entered = undefined) {
      this.chart.loading = true;
      try {
        const enteredFlag = this.resolveEnteredPartsFlag(entered);
        const response = await getRequest(
          `/history/institution/single/${this.$store.getters.institutionId}/`,
          `recent=true&details=true&period=${this.chart.timePeriod}&time=${this.chart.time}&status=${this.chart.status}&role=${this.$store.getters.role}&entered_parts=${enteredFlag}`,
        );
        const labels = response[Object.keys(response)[0]].map((element) => {
          if (this.chart.timePeriod === "Day") {
            return `${moment(element.startingTime).format("LT")} - ${moment(element.endingTime).format("LT")}`;
          } else if (this.chart.timePeriod === "Week") {
            return moment(element.startingTime).format("ddd, MMM D");
          }
          return moment(element.startingTime).format("D");
        });
        const datasets = Object.keys(response).map((key) => {
          const rnd = generateRandomNumbers();
          const dataPoints = response[key].map((element) => ({
            x: element.startingTime,
            y: element.value,
            amount: element.totalAmount ? element.totalAmount.toFixed(2) : 0,
          }));
          return {
            label: key,
            data: dataPoints,
            backgroundColor: `rgba(${rnd[0]},${rnd[1]},${rnd[2]},${this.chart.type === "line" ? "0.1" : "0.5"})`,
            borderColor: `rgba(${rnd[0]},${rnd[1]},${rnd[2]}, 1)`,
          };
        });
        this.chart.data = { labels, datasets };
      } catch {
      } finally {
        this.chart.loading = false;
      }
    },
    selectChartContent(content, status) {
      if (content === "ebay-leko" && status === "6.4") {
        this.getEbayOverview();
      }

      this.ebay_overview = false;
      this.chart.content = content;
      this.chart.status = status;
      if (status == "1") {
        this.getPastUserActivity(true);
      } else {
        this.getPastUserActivity();
      }
    },
    selectChartType(type) {
      this.chart.type = type;
      this.getPastUserActivity();
    },

    async changeInventoryPrices() {
      this.confirmDialog = true;
    },
    async confirmChangePrice() {
      this.priceChangeLoading = true;
      try {
        const res = await getRequest(
          "/part/change-price",
          `institution_id=${this.$store.getters.institutionId}&amount=${this.priceChange.percentage}&type=${this.priceChange.type}`,
        );
        if (res) {
          this.priceChangeLoading = false;
          this.successDialog = true;
          setTimeout(() => {
            this.successDialog = false;
          }, 2000);
        }
      } catch {
        this.priceChangeLoading = false;
        this.errorDialog = true;
        setTimeout(() => {
          this.errorDialog = false;
        }, 2000);
      } finally {
        this.confirmDialog = false;
        this.priceChange.percentage = 0;
      }
    },
    async fetchInstitutionDetails() {
      try {
        const response = await getRequest(
          "/institution",
          `id=${this.$store.getters.institutionId}`,
        );
        if (response.institution) {
          this.institution = response.institution[0];
          if (this.institution && this.institution.export_ebay_leko) {
            this.chart.content = "ebay-leko";
            this.chart.status = "6.4";
            this.selectChartContent("ebay-leko", "6.4");
          }
        }
      } catch {}
    },
    mapStandardLevel(level) {
      if (!level) return "Unknown";
      switch (level) {
        case "TOP_RATED":
          return "Top Rated";
        case "ABOVE_STANDARD":
          return "Above Standard";
        case "BELOW_STANDARD":
          return "Below Standard";
        default:
          return level.replace("_", " ").toLowerCase();
      }
    },
    async getEbayOverview() {
      this.ebay_overview_loader = true;
      const cachedProfiles = localStorage.getItem("standardsProfiles");
      if (cachedProfiles) {
        this.standardsProfiles = JSON.parse(cachedProfiles);
        this.populateSellerFromProfiles(this.standardsProfiles);
        this.ebay_overview_loader = false;
        return;
      }
      try {
        const response = await postRequest.object("/ebay/seller-standards", {
          institutionId: this.$store.getters.institutionId,
        });
        if (response.data.standardsProfiles) {
          this.standardsProfiles = response.data.standardsProfiles;
          localStorage.setItem(
            "standardsProfiles",
            JSON.stringify(this.standardsProfiles),
          );
          this.populateSellerFromProfiles(this.standardsProfiles);
        } else if (response.data.errorMsg) {
          this.ebay_error = response.data.errorMsg;
        }
      } catch {
      } finally {
        this.ebay_overview_loader = false;
      }
    },
    populateSellerFromProfiles(profiles) {
      const current = profiles.find((p) => p.cycle.cycleType === "CURRENT");
      const projected = profiles.find((p) => p.cycle.cycleType === "PROJECTED");
      this.seller.evaluationMonth = current.cycle.evaluationMonth || "N/A";
      this.seller.actualStandard = this.mapStandardLevel(
        current.standardsLevel,
      );
      this.seller.predictedStandard = this.mapStandardLevel(
        projected.standardsLevel,
      );
    },
    getCycle(program, cycleType) {
      return (
        this.standardsProfiles.find(
          (p) => p.program === program && p.cycle.cycleType === cycleType,
        ) || {}
      );
    },
    getMetric(cycleObj, metricKey) {
      if (
        !cycleObj ||
        !Array.isArray(cycleObj.metrics) ||
        cycleObj.metrics.length === 0
      ) {
        return {};
      }
      return cycleObj.metrics.find((m) => m.metricKey === metricKey) || {};
    },
    getStandardColor(status) {
      switch (status) {
        case "Above Standard":
          return "green";
        case "ETRS":
          return "blue";
        case "Below Standard":
          return "red";
        case "Top Rated":
          return "orange";
        default:
          return "grey";
      }
    },
    async downloadExcel() {
      try {
        this.exportEbayShippingLoading = true;

        // Step 1: Fetch backend data
        const res = await getRequest(
          `/ebay/export-shipping-data/${this.$store.getters.institutionId}`,
        );
        if (!res.success) {
          this.exportEbayShippingLoading = false;
          return;
        }

        // Step 2: Transform data
        const exportItems = res.data.map((item) => ({
          "Part Code": item.partCode || "",
          "Title (IT)": item.title_it || "",
          "Weight Range": item.weightRange || "",
          "eBay IT Cost": item.ebayItCost || 0,
          "eBay DE Cost": item.ebayDeCost || 0,
          "eBay FR Cost": item.ebayFrCost || 0,
          "eBay ES Cost": item.ebayEsCost || 0,
        }));

        // Step 3: Convert JSON → worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportItems);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ShippingData");

        // Step 4: Save file
        XLSX.writeFile(workbook, "exportEbayShippingData.xlsx");
      } catch {
      } finally {
        this.exportEbayShippingLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.fill-height {
  height: 100% !important;
}
.display-1 {
  font-size: 24px !important;
}
.change-inventory-prices-btn :deep(.v-btn__content) {
  white-space: normal;
  line-height: 1.25;
  text-align: center;
}

@media (max-width: 599px) {
  .display-1 {
    font-size: 20px !important;
  }
  .subheading {
    font-size: 11px !important;
  }
  /* Compact card-text padding on mobile */
  .fill-height :deep(.v-card-text) {
    padding: 10px 10px !important;
  }
  /* Force consistent vertical centering across all stat cards */
  .fill-height :deep(.v-card-text > .v-row) {
    align-items: center !important;
  }
  /* Neutralise mt-0/pt-0 that some icon cols have, so all icons stay centered */
  .fill-height :deep(.v-card-text > .v-row > .v-col--auto) {
    margin-top: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    flex-shrink: 0;
  }
  /* Restore right spacing on cols that strip it (price/parts cards) */
  .fill-height :deep(.v-card-text > .v-row > .v-col.pr-0),
  .fill-height :deep(.v-card-text > .v-row > .v-col.mr-0) {
    padding-right: 6px !important;
    margin-right: 0 !important;
  }
  /* Single-line label — truncate if too long */
  .fill-height :deep(.v-card-text .subheading),
  .fill-height :deep(.v-card-text .white--text:not(.display-1)) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
}
</style>
